import * as React from 'react';

import { cn, formatarData, formatarDataHora } from '@/lib/utils';
import {
  dataHoraAtendimento,
  nascimentoComIdade,
  AVISO_DOCUMENTOS,
  simNao,
  VAZIO,
} from '@/features/marcacao/formato';
import type { MarcacaoEsus } from '@/features/marcacao/tipos';

/**
 * Cor da classificação de prioridade, como o eSUS pinta: do vermelho
 * (prioridade 0) ao azul (prioridade 3).
 */
const COR_PRIORIDADE: Record<number, string> = {
  0: 'text-[#d32f2f]',
  1: 'text-[#ed8b00]',
  2: 'text-[#2e7d32]',
  3: 'text-[#2196f3]',
};

/**
 * Área útil do A4: 210 × 297 mm menos a margem de 12 mm de cada lado, que o
 * `@page` global de `index.css` define.
 */
const LARGURA_UTIL_MM = 186;
const ALTURA_UTIL_MM = 273;
/** Sobra no pé: meio milímetro de arredondamento não pode abrir uma 2ª página. */
const FOLGA_MM = 3;
const PX_POR_MM = 96 / 25.4;

/**
 * Folha A4 no layout da impressão do eSUS Regulação. Cores fixas, de papel,
 * independentemente do tema da tela.
 *
 * Sai sempre em **uma página**. A folha tem a largura da área útil também na
 * tela — o que se vê é o que se imprime —, e por isso a altura medida aqui é
 * a do papel. O layout já é compacto para caber; se ainda passar, como num
 * exame com muitos procedimentos, a folha é reduzida em escala até caber, em
 * vez de abrir uma segunda página.
 */
export function FolhaMarcacao({
  marcacao,
  chaveConfirmacao,
  impressoPor,
}: {
  marcacao: MarcacaoEsus;
  /** Digitada por quem imprime: o índice do eSUS não a traz. */
  chaveConfirmacao: string | null;
  impressoPor: string | null;
}) {
  const conteudo = React.useRef<HTMLElement>(null);
  const [alturaMm, setAlturaMm] = React.useState<number | null>(null);

  // A escala é um `transform`, que não altera `offsetHeight`: medir de novo
  // depois de reduzir não realimenta a conta.
  React.useLayoutEffect(() => {
    const elemento = conteudo.current;
    if (!elemento) return;

    const medir = () => setAlturaMm(elemento.offsetHeight / PX_POR_MM || null);
    medir();

    if (typeof ResizeObserver === 'undefined') return;
    const observador = new ResizeObserver(medir);
    observador.observe(elemento);
    return () => observador.disconnect();
  }, []);

  const limite = ALTURA_UTIL_MM - FOLGA_MM;
  const escala = alturaMm === null ? 1 : Math.min(1, limite / alturaMm);

  const { agendamento: ag, paciente: pac, condicoesClinicas: cc, solicitante: sol } = marcacao;

  return (
    // Largura fixa de papel: no celular a folha rola de lado, em vez de
    // reorganizar as colunas e mentir sobre como sai impressa.
    <div className="overflow-x-auto print:overflow-visible">
      <div
        className="mx-auto overflow-hidden bg-white shadow-sm print:shadow-none"
        style={{
          width: `${LARGURA_UTIL_MM}mm`,
          // Só encurta quando reduz: a caixa acompanha a folha já em escala.
          height: escala < 1 ? `${limite}mm` : undefined,
        }}
      >
        <article
          ref={conteudo}
          aria-label="Comprovante de marcação"
          className="bg-white text-[11px] leading-tight text-[#333]"
          style={{
            width: `${LARGURA_UTIL_MM}mm`,
            transform: escala < 1 ? `scale(${escala})` : undefined,
            transformOrigin: 'top center',
          }}
        >
          <Secao titulo="Identificador da solicitação">
            <Campo rotulo="Identificador" largura="metade">
              <span className="text-[14px] font-normal text-[#1e73be]">
                {marcacao.identificador}
              </span>
            </Campo>
            {/* Não existe no índice do eSUS: vem digitada no modal, ou sai em branco. */}
            <Campo
              rotulo="Chave de Confirmação"
              largura="metade"
              valor={chaveConfirmacao}
              destaque
            />
          </Secao>

          <Secao titulo="Agendamento">
            <Campo rotulo="Nome do Estabelecimento Executante" valor={ag.estabelecimento} />
            <Campo rotulo="CNES" valor={ag.cnes} />
            <Campo rotulo="Telefone" valor={ag.telefone} />
            <Campo rotulo="Endereço" valor={ag.logradouro} />
            <Campo rotulo="Número" valor={ag.numero} />
            <Campo rotulo="Complemento" valor={ag.complemento} />
            <Campo rotulo="Bairro" valor={ag.bairro} />
            <Campo rotulo="Município" valor={ag.municipio} />
            <Campo rotulo="CEP" valor={ag.cep} />
            <Campo rotulo="Profissional Executante" valor={ag.profissional} largura="metade" />
            <Campo rotulo="Data e Horário do Atendimento" largura="metade">
              <span className="text-[14px] font-bold text-[#2e7d32]">
                {dataHoraAtendimento(ag) ?? VAZIO}
              </span>
            </Campo>
          </Secao>

          <Secao
            titulo={`Atendimento para ${marcacao.modulo?.toLowerCase() === 'exame' ? 'exame' : 'consulta'}`}
            semGrade
          >
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-[#ddd] text-left text-[11px] font-normal uppercase text-[#333]">
                  <th className="w-[57%] px-4 py-1 font-normal" />
                  <th className="w-[20%] px-2 py-1 font-normal">Código Interno</th>
                  <th className="px-2 py-1 font-normal">Código Unificado</th>
                </tr>
              </thead>
              <tbody>
                {marcacao.procedimentos.length === 0 ? (
                  <tr>
                    <td className="px-4 py-1 font-semibold" colSpan={3}>
                      {VAZIO}
                    </td>
                  </tr>
                ) : (
                  marcacao.procedimentos.map((p, i) => (
                    <tr key={i} className="text-[11px] font-semibold uppercase text-[#222]">
                      <td className="px-4 py-1">{p.nome ?? VAZIO}</td>
                      <td className="px-2 py-1">{p.codigoInterno ?? VAZIO}</td>
                      <td className="px-2 py-1">{p.codigoUnificado ?? VAZIO}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </Secao>

          {/* O índice do eSUS não traz o preparo: a seção fica, vazia, como no eSUS. */}
          <Secao titulo="Preparo(s) para o procedimento(s)" semGrade>
            <div className="h-1.5" />
          </Secao>

          <Secao titulo="Dados do usuário" espacado>
            <Campo rotulo="CNS" valor={pac.cns} />
            <Campo rotulo="CPF (sem máscara na numeração)" valor={pac.cpf} />
            <Campo rotulo="Data de Nascimento" valor={nascimentoComIdade(pac.dataNascimento)} />
            <Campo rotulo="Nome" valor={pac.nome} />
            <Campo rotulo="Nome Social" valor={pac.nomeSocial} />
            <Campo rotulo="Tipo Sanguíneo" valor={pac.tipoSanguineo} />
            <Campo rotulo="Nome da Mãe" valor={pac.nomeMae} />
            <Campo rotulo="Sexo" valor={pac.sexo} />
            <Campo rotulo="Raça" valor={pac.racaCor} />
            <Campo rotulo="Logradouro" valor={pac.logradouro} />
            <Campo rotulo="Complemento" valor={pac.complemento} />
            <Campo rotulo="Etnia Indígena" valor={pac.etniaIndigena} />
            <Campo rotulo="Bairro" valor={pac.bairro} />
            <Campo rotulo="Município de Residência" valor={pac.municipio} />
            <Campo rotulo="Número" valor={pac.numero} />
            <Campo rotulo="CEP" valor={pac.cep} />
            <Campo
              rotulo="Telefone(s)"
              valor={pac.telefones.length > 0 ? pac.telefones.join(', ') : null}
              largura="doisTercos"
            />
          </Secao>

          <Secao titulo="Condições clínicas">
            <Campo rotulo="Justificativa" valor={cc.justificativa} largura="inteira" />
            <Campo rotulo="Descrição da Classificação" valor={cc.descricaoCid} largura="metade" />
            <div className="col-span-3 flex flex-col gap-1">
              <Campo rotulo="CID-10" valor={cc.cid} largura="contido" />
              {/* CIAP não existe no índice do eSUS. */}
              <Campo rotulo="CIAP" valor={null} largura="contido" />
            </div>
            <Campo rotulo="Classificação de prioridade" largura="quarto">
              <span
                className={cn(
                  'text-[14px] font-bold normal-case',
                  COR_PRIORIDADE[cc.codigoClassificacaoRisco ?? 3] ?? COR_PRIORIDADE[3],
                )}
              >
                {cc.classificacaoRisco ?? VAZIO}
              </span>
            </Campo>
            <Campo rotulo="Oncológico" valor={simNao(cc.oncologico)} />
            <Campo rotulo="Diabetes" valor={simNao(cc.diabetes)} />
            <Campo rotulo="Alergia" valor={simNao(cc.alergia, cc.descricaoAlergia)} />
            <Campo rotulo="Hipertensão Arterial" valor={simNao(cc.hipertensao)} />
            <Campo
              rotulo="Usuário com Deficiência"
              valor={simNao(cc.deficiencia, cc.descricaoDeficiencia)}
            />
            <Campo rotulo="OCI (Oferta de Cuidados Integrados)" valor={simNao(cc.oci)} />
          </Secao>

          <Secao titulo="Solicitante">
            <Campo rotulo="Profissional" valor={sol.profissional} largura="metade" />
            <Campo rotulo="Conselho Profissional" valor={sol.conselho} largura="quarto" />
            <Campo rotulo="CPF (sem máscara na numeração)" valor={sol.cpf} largura="quarto" />
            <Campo rotulo="Nome do Estabelecimento" valor={sol.estabelecimento} largura="metade" />
            <Campo rotulo="CNES" valor={sol.cnes} largura="quarto" />
            <Campo rotulo="Telefone" valor={sol.telefone} largura="quarto" />
          </Secao>

          <Secao titulo="Dados da solicitação">
            <Campo
              rotulo="Data do pedido"
              valor={
                marcacao.dadosSolicitacao.dataPedido &&
                formatarData(marcacao.dadosSolicitacao.dataPedido)
              }
              largura="quarto"
            />
            <Campo
              rotulo="Data do Cadastro"
              valor={
                marcacao.dadosSolicitacao.dataCadastro &&
                formatarData(marcacao.dadosSolicitacao.dataCadastro)
              }
              largura="quarto"
            />
            <Campo
              rotulo="Tipo de Consulta"
              valor={marcacao.dadosSolicitacao.tipoConsulta}
              largura="quarto"
            />
            <Campo rotulo="Situação Atual" largura="quarto">
              <span className="text-[14px] font-bold normal-case text-[#222]">
                {marcacao.situacao ?? VAZIO}
              </span>
            </Campo>
            <Campo
              rotulo="Telessaúde"
              valor={simNao(marcacao.dadosSolicitacao.telessaude)}
              largura="quarto"
            />
          </Secao>

          {/* Fora do cinza das seções, de propósito: é a parte que o paciente
          precisa ler, e tem de saltar aos olhos também em impressora P&B. */}
          <aside className="mx-4 mt-2 break-inside-avoid rounded-sm border-2 border-[#222] px-3 py-1.5 text-[#111]">
            <p className="text-[13px] font-bold">{AVISO_DOCUMENTOS.titulo}</p>
            <ul className="mt-0.5 flex list-disc flex-col gap-px pl-5 text-[12px] font-semibold">
              {AVISO_DOCUMENTOS.itens.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </aside>

          <p className="px-4 pt-1 text-right text-[9px] text-[#888]">
            Dados do eSUS Regulação · impresso pelo KAVI em {formatarDataHora(new Date())}
            {impressoPor ? ` por ${impressoPor}` : ''}
          </p>
        </article>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------

function Secao({
  titulo,
  children,
  semGrade,
  espacado,
}: {
  titulo: string;
  children: React.ReactNode;
  semGrade?: boolean;
  espacado?: boolean;
}) {
  return (
    <section className="break-inside-avoid">
      <h2 className="border-y border-[#ddd] bg-[#eee] px-4 py-[2px] text-[11.5px] font-bold uppercase tracking-wide text-[#555]">
        {titulo}
      </h2>
      {semGrade ? (
        children
      ) : (
        <dl
          className={cn('grid grid-cols-12 gap-x-3 px-4 py-1', espacado ? 'gap-y-1' : 'gap-y-0.5')}
        >
          {children}
        </dl>
      )}
    </section>
  );
}

/** Colunas fixas, sem quebra responsiva: a folha tem sempre a largura do papel. */
const LARGURAS = {
  terco: 'col-span-4',
  doisTercos: 'col-span-8',
  metade: 'col-span-6',
  quarto: 'col-span-3',
  inteira: 'col-span-12',
  contido: '',
} as const;

function Campo({
  rotulo,
  valor,
  largura = 'terco',
  destaque,
  children,
}: {
  rotulo: string;
  valor?: string | null;
  largura?: keyof typeof LARGURAS;
  /** Valor em corpo maior, como o eSUS imprime a chave de confirmação. */
  destaque?: boolean;
  children?: React.ReactNode;
}) {
  return (
    <div className={cn('min-w-0 break-words', LARGURAS[largura])}>
      <dt className="text-[10.5px] font-normal tracking-wide text-[#666]">{rotulo}</dt>
      <dd
        className={cn(
          'font-semibold uppercase text-[#222]',
          destaque ? 'text-[14px] font-bold' : 'text-[11px]',
        )}
      >
        {children ?? (valor?.trim() ? valor : VAZIO)}
      </dd>
    </div>
  );
}
